/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import './ProductTypePerformanceChart.scss';

import { forEach, isNil, map, reduce } from 'lodash';
import * as d3 from 'd3';
import React from 'react';
import { connect } from 'react-redux';

import Store from 'state/Store';
import { toggledPrrChart } from 'state/Actions';
import { svgWidth } from 'components/shared/ChartHelper/ChartHelper';
import {
  registerRenderOnWindowResize,
  unregisterRenderOnWindowResize
} from 'components/shared/ComponentHelper/ComponentHelper';
import { percentValue } from 'services/Formats';
import {
  filterAccountsOnProductClass,
  getSelectedProductClasses,
  ACTIVE_PLUS_CLASS,
  isAdviceProductClass
} from 'services/Products';
import {
  getClassForProductType,
  isBenchmark,
  getProductTypes,
  isPersonalInvestment,
  getBarComponentId
} from 'state/performance/ProductPerformanceSelectors';
import DisclosureModal from 'components/shared/Disclosure/DisclosureModal';
import Content from 'services/Content';
import {
  getRateOfReturnFilter,
  getShowAllPRRGraphWarningsForRateOfReturn,
  getShowMultipleActivePlusForRateOfReturn
} from 'state/performance/RateOfReturnFilterSelectors';
import { filter } from 'lodash';
import { onAccountDetailsPage } from 'state/view/ViewSelectors';

class ProductTypePerformanceChart extends React.Component {
  constructor(props) {
    super(props);

    this.perfData = this.perfData.bind(this);
  }

  componentDidMount() {
    Store.dispatch(toggledPrrChart('product'));
    registerRenderOnWindowResize(this);
  }

  componentWillUnmount() {
    unregisterRenderOnWindowResize(this);
  }

  perfData(perfPeriods, perfAggregate) {
    return perfPeriods.map((period) => {
      const perf = { ...period };
      const rate = (perf.rate =
        perfAggregate[perf.key] === '-' ? null : perfAggregate[perf.key]);
      if (!rate) {
        perf.rate = 0;
        perf.rateClass = 'not-available';
        perf.formattedRate = isNil(rate) ? 'N/A' : percentValue(rate);
      } else {
        if (rate >= 0.00005) {
          perf.rateClass = 'positive';
        } else if (rate <= -0.00005) {
          perf.rateClass = 'negative';
        } else {
          perf.rateClass = 'zero';
        }
        perf.formattedRate = percentValue(rate);
      }
      perf.product = perfAggregate.productType;
      return perf;
    });
  }

  renderChartIntoRef(selectedPerf, svgElement) {
    if (svgElement == null) {
      return;
    }

    const { performancePeriods } = this.props;

    const bigTypes = map(selectedPerf, 'productType');

    const bigData = map(selectedPerf, (prodPerf) => {
      return this.perfData(performancePeriods, prodPerf);
    });

    const labelFn = (d) => d.label;
    const rateFn = (d) => d.rate;
    const formattedRateFn = (d) => d.formattedRate;

    const labels = performancePeriods.map(labelFn);

    const SVG_DEFAULT_FULL_WIDTH = 829;

    const fullWidth = svgWidth(svgElement);
    const fullHeight = 379;
    const isReducedSize = fullWidth < SVG_DEFAULT_FULL_WIDTH;

    const Y_TICK_PADDING = 12;

    let minData = reduce(
      bigData,
      (min, perf) => {
        const dataMin = d3.min(perf, rateFn);
        return min < dataMin ? min : dataMin;
      },
      0
    );
    let maxData = reduce(
      bigData,
      (max, perf) => {
        const dataMax = d3.max(perf, rateFn);
        return max > dataMax ? max : dataMax;
      },
      0
    );

    minData = Math.min(0, minData * 1.1);
    maxData = Math.max(0, maxData * 1.1);

    const meanData = (minData + maxData) / 2;

    // formula gives us 6px per digit + 25 px
    const leftMargin = Math.ceil(
      Y_TICK_PADDING +
        Math.ceil(Math.log10(maxData !== 0 ? maxData : 1)) * 6 +
        25
    );

    const tickPadding = 20;
    const yAxisOffset = 12;

    const margin = {
      top: 18,
      right: 5,
      bottom: isReducedSize ? 60 : 48,
      left: leftMargin + tickPadding
    };

    const plotWidth = fullWidth - margin.left - margin.right;
    const plotHeight = fullHeight - margin.top - margin.bottom;

    const xAxisLeft = margin.left - 3;
    const xAxisTop = fullHeight - margin.bottom + 24;

    const x = d3.scaleBand().rangeRound([0, plotWidth]).padding(0.3);
    const y = d3.scaleLinear().range([plotHeight, 0]);

    x.domain(labels);
    y.domain([minData, maxData]).nice(5).domain();

    const y0 = y(0);

    const barGap = 3;
    const gapWidth = bigData.length > 1 ? bigData.length * barGap : 0;
    const barWidth = (x.bandwidth() - gapWidth) / bigData.length;

    const xAxis = d3.axisBottom(x).tickValues(labels).tickSize(null);

    const yAxis = d3
      .axisLeft(y)
      .ticks(5)
      .tickSize(-plotWidth)
      .tickPadding(tickPadding);

    const svg = d3
      .select(svgElement)
      .attr('width', fullWidth)
      .attr('height', fullHeight)
      .attr('viewBox', `0 0 ${fullWidth} ${fullHeight}`);

    // remove old contents if present
    svg.selectAll('*').remove();

    svg
      .append('rect')
      .attr('class', 'backgroundLowerHalf')
      .attr('x', margin.left)
      .attr('y', y0 + margin.top)
      .attr('width', plotWidth)
      .attr('height', plotHeight - y0);

    const xAxis3 = svg
      .append('g')
      .attr('class', 'x axis')
      .attr('transform', `translate(${xAxisLeft}, ${xAxisTop})`)
      .call(xAxis);

    if (isReducedSize) {
      xAxis3
        .selectAll('text')
        .attr('y', 0)
        .attr('x', 9)
        .call(wrap, x.bandwidth());
    }

    svg
      .append('g')
      .attr('class', 'y axis')
      .attr('transform', `translate(${margin.left}, ${margin.top})`)
      .call(yAxis);

    svg
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('transform', 'rotate(-90)')
      .attr('font-weight', 'bold')
      .attr('y', yAxisOffset)
      .attr('x', -(fullHeight / 2))
      .text('Personal Rate of Return (%)');

    const plotArea = svg
      .append('g')
      .attr('class', 'plotarea bars')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    let i = 0;
    let barOffsetX = 0;

    const triangleSize = 35;
    const tooltipDims = {
      width: 64,
      height: 40,
      radius: 4
    };

    // Tooltip Selectors
    const selectTooltip = () => svg.select('#tooltip');
    const selectTooltipTriangle = () => svg.select('#tooltip-triangle');
    const selectTooltipText = () => svg.select('#tooltip-text');

    // Bar selectors
    const selectBarComponent = (productType, idx) =>
      svg.select(`#${getBarComponentId(productType, idx)}`);

    // Tooltip Utils
    const getTriangleHeight = () =>
      selectTooltipTriangle().node().getBBox().height;
    const getHalfTriangleHeight = () => getTriangleHeight() / 2;

    // Tooltip Visibility Helpers
    const setTooltipVisibility = (visibility) =>
      selectTooltip().attr('visibility', visibility);
    const showTooltip = () => setTooltipVisibility('visible');
    const hideTooltip = () => setTooltipVisibility('hidden');

    // Determine orientation of tooltip based on mean data vs rate class
    const shouldFlipTooltip = (d) => d.rate < meanData;

    // Get tooltip position and offset
    const transformTooltip = (d, bar) => {
      const shouldFlip = shouldFlipTooltip(d);
      const halfTriangleHeight = getHalfTriangleHeight();
      const Y_OFFSET =
        halfTriangleHeight + (shouldFlip ? tooltipDims.height : 0);

      const barBounds = bar.node().getBBox();
      const translateX =
        barBounds.x + (barBounds.width - tooltipDims.width) / 2;
      const translateY = y(d.rate) + (shouldFlip ? -1 : 1) * Y_OFFSET;

      return `translate(${translateX},${translateY})`;
    };

    // Get triangle position, offset, and rotation
    const transformTriangle = (d) => {
      const shouldFlip = shouldFlipTooltip(d);
      const translateX = tooltipDims.width / 2;
      const translateY = shouldFlip ? tooltipDims.height : 0;
      const deg = shouldFlip ? 180 : 0;

      const translate = `translate(${translateX}, ${translateY})`;
      const rotate = `rotate(${deg})`;

      const transformations = [translate, rotate];

      return transformations.join(' ');
    };

    // Show, Move, and Update Tooltip
    const setTooltip = (d, i, productType) => {
      // 1. Set Visibility
      showTooltip();

      // 2. Rotate Triangle
      selectTooltipTriangle().attr('transform', transformTriangle(d));

      // 3. Move Tooltip
      const bar = selectBarComponent(productType, i);
      selectTooltip().attr('transform', transformTooltip(d, bar));

      // 4. Update Text
      selectTooltipText().text(formattedRateFn(d));
    };

    // Event handlers
    const onMouseOver = (productType) => (d, i) =>
      setTooltip(d, i, productType);
    const onMouseLeave = () => hideTooltip();

    // First loop to draw bar components
    forEach(bigData, (data) => {
      const selectAll = '.' + bigTypes[i];
      const productType = bigTypes[i];
      const xLabelFn = (d) => {
        return x(d.label) + barOffsetX;
      };

      plotArea
        .selectAll(selectAll)
        .data(data)
        .enter()
        .append('rect')
        .attr('class', (d) =>
          getClassForProductType(selectedPerf, productType, d.rateClass)
        )
        .attr('id', (d, idx) => getBarComponentId(productType, idx))
        .attr('x', xLabelFn)
        .attr('y', (d) => y(Math.max(0, d.rate)))
        .attr('width', barWidth)
        .attr('height', (d) => Math.abs(y(d.rate) - y0))
        .on('mouseover', onMouseOver(productType))
        .on('mouseleave', onMouseLeave)
        .exit();

      i++;
      barOffsetX += barWidth + barGap;
    });

    plotArea
      .append('line')
      .attr('class', 'zero-line')
      .attr('x1', 0)
      .attr('x2', plotWidth)
      .attr('y1', y0)
      .attr('y2', y0);

    barOffsetX = 0;
    i = 0;

    // Append Tooltip
    const triangle = d3.symbol().type(d3.symbolTriangle).size(triangleSize);
    const tooltip = plotArea
      .append('g')
      .attr('id', 'tooltip')
      .attr('visibility', 'hidden');

    tooltip
      .append('rect')
      .attr('width', tooltipDims.width)
      .attr('height', tooltipDims.height)
      .attr('rx', tooltipDims.radius)
      .attr('ry', tooltipDims.radius);

    tooltip.append('path').attr('id', 'tooltip-triangle').attr('d', triangle);

    tooltip
      .append('text')
      .attr('id', 'tooltip-text')
      .attr('x', tooltipDims.width / 2)
      .attr('y', tooltipDims.height / 2 + getHalfTriangleHeight());

    // [ -] or \s
    function wrap(text, width) {
      text.each(function () {
        var text = d3.select(this),
          words = text.text().split(/[ -]+/).reverse(),
          word,
          line = [],
          lineNumber = 0,
          lineHeight = 1.1, // ems
          y = text.attr('y'),
          dy = parseFloat(text.attr('dy')),
          tspan = text
            .text(null)
            .append('tspan')
            .attr('x', 0)
            .attr('y', y)
            .attr('dy', dy + 'em');
        while ((word = words.pop())) {
          // eslint-disable-line no-cond-assign
          line.push(word);
          tspan.text(line.join(' '));
          if (tspan.node().getComputedTextLength() > width) {
            line.pop();
            tspan.text(line.join(' '));
            line = [word];
            tspan = text
              .append('tspan')
              .attr('x', 0)
              .attr('y', y)
              .attr('dy', ++lineNumber * lineHeight + dy + 'em')
              .text(word);
          }
        }
      });
    }
  }

  render() {
    const {
      isAccountDetails,
      productPerformance,
      productTypes,
      performanceFilter,
      showMultipleActivePlus,
      showAllPRRGraphWarnings
    } = this.props;

    if (productPerformance === undefined || productPerformance.length === 0) {
      return null;
    }

    const selectedValueFilter = performanceFilter.filter.label;
    const selectedProductTypes = getSelectedProductClasses(selectedValueFilter);

    // if model benchmark is in the data, it should stay in the data no matter what
    let selectedPerf = isAccountDetails
      ? productPerformance
      : filter(productPerformance, (prodPerf) => {
          return (
            !showAllPRRGraphWarnings &&
            (selectedProductTypes.includes(prodPerf.productType) ||
              isBenchmark(prodPerf.productType) ||
              isPersonalInvestment(prodPerf.productType))
          );
        });

    if (showMultipleActivePlus) {
      selectedPerf = filterAccountsOnProductClass(
        selectedPerf,
        ACTIVE_PLUS_CLASS,
        true
      );
    }

    const legendItems =
      selectedPerf.length > 1 &&
      map(selectedPerf, (product) => {
        const { productType, accountId } = product;
        const label = isBenchmark(productType)
          ? 'Model Benchmark'
          : productTypes[productType].label;
        const disclosure = (
          <DisclosureModal content={Content.rasPRRDisclosure} hideLinkContent />
        );
        return (
          <div
            key={`${accountId}${productType}`}
            className={`text legend-item ${getClassForProductType(
              selectedPerf,
              productType
            )}`}>
            {label}
            {isAdviceProductClass(productType) && disclosure}
          </div>
        );
      });

    return (
      <div
        id="product-performance-chart"
        className={`performance-chart${
          isAccountDetails ? '-account-details' : ''
        }`}>
        <svg ref={this.renderChartIntoRef.bind(this, selectedPerf)} />
        <div className="legend4">{legendItems}</div>
      </div>
    );
  }
}

ProductTypePerformanceChart.propTypes = {};

const mapStateToProps = (state) => {
  return {
    isAccountDetails: onAccountDetailsPage(state),
    productTypes: getProductTypes(state),
    performanceFilter: getRateOfReturnFilter(state),
    showMultipleActivePlus: getShowMultipleActivePlusForRateOfReturn(state),
    showAllPRRGraphWarnings: getShowAllPRRGraphWarningsForRateOfReturn(state)
  };
};

export default connect(mapStateToProps)(ProductTypePerformanceChart);
